import { Head, router, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { accountSwitchPath, newAccountPath } from "@travily/routes";
import type ProgramsIndex from "@travily/types/pages/ProgramsIndex";
import { useMemo, useState } from "react";
import { AddPersonSheet } from "@/components/AddPersonSheet/AddPersonSheet";
import { SearchRoot } from "@/components/Search/SearchRoot";
import { useSearchBarWhen } from "@/hooks/use-search-bar-when";
import { useSearchBarWhere } from "@/hooks/use-search-bar-where";
import { useSearchBarWho } from "@/hooks/use-search-bar-who";
import { useFavoritedIdsCallbacks } from "@/hooks/use-search-favorites";
import { useSearchSessionUiState } from "@/hooks/use-search-session";
import { ExploreFiltersProvider } from "@/layouts/components/explore-filters-context";
import { PageFrame } from "@/layouts/page-frame";
import { parseWhenParams, parseWhereParams, parseWhoParams } from "@/lib/search-params";
import type { SharedProps } from "@/types";
import { ProgramsIndexView } from "./index.view";
import { ProgramsFiltersAdapter, ProgramsFiltersFooterAdapter } from "./ProgramsFiltersAdapter";
import { ProgramsSearchAdapter } from "./ProgramsSearchAdapter";
import { ProgramsUrlSync } from "./ProgramsUrlSync";
import { hasProgramsSearchParams, toProgramsUiState } from "./search-params";
import { useProgramsSearch } from "./useProgramsSearch";

const ProgramsIndexPage = ({ favorited_ids, seed_results, seed_total }: ProgramsIndex) => {
  const page = usePage<SharedProps>();
  const { current_account, accounts, family_members } = page.props;
  const isFamily = current_account?.profile_type === "Family";
  // A non-family viewer who already owns a family account is offered a switch;
  // otherwise the upsell points them at creating one.
  const familyAccount = useMemo(() => accounts?.find((account) => account.profile_type === "Family"), [accounts]);

  // Parsed once per mount: shared links, back/forward (Inertia remounts on
  // popstate), and the mobile overlay hand-off all rehydrate through it.
  // Who drops ids that aren't in this account's members (stale/cross-account
  // URL) so the pill never shows an "active" selection that resolves to no
  // members (and no filter).
  const [initialSearch] = useState(() => {
    const seededWho = parseWhoParams(page.url);
    const knownIds = new Set(family_members.map((member) => member.id));
    const memberIds = seededWho?.memberIds.filter((id) => knownIds.has(id)) ?? [];
    return {
      where: parseWhereParams(page.url) ?? undefined,
      when: parseWhenParams(page.url) ?? undefined,
      who: memberIds.length > 0 ? { memberIds } : undefined,
    };
  });

  const { whenProps, whenWindow } = useSearchBarWhen(initialSearch.when);
  const { whoProps, whoAges } = useSearchBarWho(family_members, initialSearch.who);
  const [addPersonPresented, setAddPersonPresented] = useState(false);
  const search = useProgramsSearch(whenWindow, whoAges);
  const { getFavorited, onFavoriteToggle } = useFavoritedIdsCallbacks(favorited_ids);
  // A URL carrying any owned search param wins over the restored session;
  // ProgramsUrlSync keeps the URL current from then on.
  const { initialUiState, onStateChange } = useSearchSessionUiState("programs", {
    urlUiState: toProgramsUiState(page.url, search.indexName),
    hasUrlOverride: hasProgramsSearchParams(page.url),
  });
  const { whereProps, whereBoundsBox, whereBbox, markMapArea } = useSearchBarWhere(
    initialSearch.where ? { initial: initialSearch.where } : undefined,
  );
  // Seed only the untouched default page: a shared link carrying search params
  // would flash the default grid before its own results land.
  const [seedPrograms] = useState(() => (hasProgramsSearchParams(page.url) ? [] : seed_results));
  const [seedTotal] = useState(() => (hasProgramsSearchParams(page.url) ? null : seed_total));

  // SearchBar lives inside the TopBar's `bottomContent` slot so it persists across
  // scrolls and the map's full-viewport calc adapts via --frame-topbar-h.
  const renderFiltersFooter = (close: () => void) => <ProgramsFiltersFooterAdapter onApply={close} />;

  const searchBar = (
    <ProgramsIndexView
      addPersonSheet={
        <AddPersonSheet
          onAdded={(personId) => whoProps.onChange({ memberIds: [...whoProps.value.memberIds, personId] })}
          onPresentedChange={setAddPersonPresented}
          presented={addPersonPresented}
        />
      }
      filters={<ProgramsFiltersAdapter />}
      hasFamilyAccount={!!familyAccount}
      isFamily={isFamily}
      members={family_members}
      onAddPerson={isFamily ? () => setAddPersonPresented(true) : undefined}
      onCreateFamily={() => router.visit(newAccountPath())}
      onSwitchToFamily={
        familyAccount ? () => router.post(accountSwitchPath(), { account_id: familyAccount.id }) : undefined
      }
      renderFiltersFooter={renderFiltersFooter}
      whenProps={whenProps}
      whereProps={whereProps}
      whoProps={whoProps}
    />
  );

  return (
    <SearchRoot initialUiState={initialUiState} onStateChange={onStateChange} search={search}>
      <ProgramsUrlSync
        when={whenProps.value}
        whereBbox={whereBbox}
        whereValue={whereProps.value}
        who={whoProps.value}
      />
      <ExploreFiltersProvider filters={<ProgramsFiltersAdapter />} footer={renderFiltersFooter} size="lg">
        <PageFrame
          contentMaxWidth="full"
          contentPadded={false}
          footerFullBleed
          mobileTopBarShadow="always"
          topBarBottomContent={searchBar}
        >
          <Head title={t("web.programs.index.meta_title")}>
            <meta name="description" content={t("web.programs.index.meta_description")} />
          </Head>
          <ProgramsSearchAdapter
            getFavorited={getFavorited}
            markMapArea={markMapArea}
            onFavoriteToggle={onFavoriteToggle}
            seedPrograms={seedPrograms}
            seedTotal={seedTotal}
            whenWindow={whenWindow}
            whereBbox={whereBbox}
            whereBoundsBox={whereBoundsBox}
            whereValue={whereProps.value}
            whoAges={whoAges}
          />
        </PageFrame>
      </ExploreFiltersProvider>
    </SearchRoot>
  );
};

export default ProgramsIndexPage;
